import { useEffect, useState, type ReactNode } from "react";

import type { ClubView, LivreRow } from "../game/types";
import { Crest, Money, Panel, PositionChip, StrengthBadge } from "../ui/atoms";
import { CabecalhoDaTabela, CelulasDaLinha, ControlesDaTabela, LinhaVazia, useTabela, type Coluna } from "../ui/tabela";
import { rotuloDeCaracteristica, useT } from "../i18n";

/**
 * Os Livres.
 *
 * Só gente que algum clube dispensou, com quem dispensou, há quanto tempo e
 * quando sai do jogo: mais de 32 anos sai depois de dez rodadas sem clube, e
 * os demais no fim da temporada. Clicar na linha abre a ficha, e é por ela que
 * se contrata, como qualquer contratação.
 */
export function LivresPanel({
  view,
  carregar,
  onOpenPlayer,
  selecionado,
}: {
  view: ClubView;
  carregar: () => Promise<LivreRow[]>;
  onOpenPlayer: (playerId: string) => void;
  /** O livre aberto na ficha ao lado, quando ela existe. */
  selecionado?: string | undefined;
}): ReactNode {
  const t = useT();
  const [livres, setLivres] = useState<LivreRow[] | null>(null);

  // A lista muda com a rodada e com cada contratação, daqui ou da ficha.
  const chave = `${view.season}:${view.round}:${view.squad.length}:${view.timeB.size}`;
  useEffect(() => {
    let vivo = true;
    void carregar().then((lista) => {
      if (vivo) setLivres(lista);
    });
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave]);

  const tabela = useTabela<LivreRow>({
    colunas: useColunasDosLivres(),
    procurar: (livre) => livre.name,
    destaqueInicial: "forca",
    ordemInicial: { chave: "forca", direcao: "desc" },
  });
  const linhas = tabela.preparar(livres ?? []);

  return (
    <Panel
      title={t("livres.titulo")}
      action={<span className="label-muted">{t("livres.total", { total: livres?.length ?? 0 })}</span>}
      padded={false}
    >
      <div className="scroll" style={{ maxHeight: "calc(100vh - 300px)" }}>
        <ControlesDaTabela tabela={tabela} />
        <table className="fixa">
          <CabecalhoDaTabela tabela={tabela} />
          <tbody>
            {linhas.map((livre) => (
              <tr
                key={livre.id}
                className="player"
                aria-selected={selecionado === undefined ? undefined : livre.id === selecionado}
                tabIndex={0}
                onClick={() => onOpenPlayer(livre.id)}
                onKeyDown={(evento) => {
                  if (evento.key === "Enter" || evento.key === " ") {
                    evento.preventDefault();
                    onOpenPlayer(livre.id);
                  }
                }}
                style={{ cursor: "pointer" }}
              >
                <CelulasDaLinha tabela={tabela} linha={livre} />
              </tr>
            ))}
            {livres !== null && linhas.length === 0 && <LinhaVazia tabela={tabela} />}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function useColunasDosLivres(): Array<Coluna<LivreRow>> {
  const t = useT();
  return [
    {
      chave: "pos",
      rotulo: t("mercado.colunas.pos"),
      presenca: "sempre",
      largura: 56,
      celula: (livre) => <PositionChip pos={livre.pos} />,
    },
    {
      chave: "jogador",
      rotulo: t("mercado.colunas.jogador"),
      presenca: "sempre",
      ordenar: (livre) => livre.name,
      direcaoInicial: "asc",
      celula: (livre) => (
        <div style={{ minWidth: 0 }}>
          <div className="corta" style={{ fontWeight: 600 }}>
            {livre.name}
          </div>
          <div className="muted corta" style={{ fontSize: 10.5 }}>
            {t("mercado.linhaSecundaria", {
              idade: livre.age,
              destaques: livre.highlights.map((chave) => rotuloDeCaracteristica(chave)).join(" · "),
            })}
          </div>
        </div>
      ),
    },
    {
      chave: "forca",
      rotulo: t("mercado.colunas.forca"),
      presenca: "destaque",
      largura: 46,
      direita: true,
      ordenar: (livre) => livre.strength,
      celula: (livre) => <StrengthBadge value={livre.strength} />,
    },
    {
      chave: "origem",
      rotulo: t("livres.colunas.origem"),
      presenca: "destaque",
      ordenar: (livre) => livre.fromClub?.name ?? "",
      direcaoInicial: "asc",
      celula: (livre) =>
        livre.fromClub ? (
          <span className="row" style={{ gap: 6, minWidth: 0 }}>
            <Crest colors={livre.fromClub.colors} short={livre.fromClub.short} size={20} />
            <span className="corta">{livre.fromClub.name}</span>
          </span>
        ) : (
          <span className="muted">{t("comum.vazio")}</span>
        ),
    },
    {
      chave: "ha",
      rotulo: t("livres.colunas.livreHa"),
      presenca: "destaque",
      largura: 90,
      direita: true,
      ordenar: (livre) => livre.rounds,
      direcaoInicial: "asc",
      celula: (livre) => (
        <span className="num" style={{ color: livre.rounds >= 5 ? "var(--warning)" : "var(--text-muted)" }}>
          {livre.rounds === 0 ? t("livres.nestaRodada") : t("livres.rodadas", { rodadas: livre.rounds })}
        </span>
      ),
    },
    {
      chave: "sai",
      rotulo: t("livres.colunas.sai"),
      presenca: "destaque",
      largura: 132,
      direita: true,
      ordenar: (livre) => livre.leavesIn ?? 999,
      direcaoInicial: "asc",
      celula: (livre) =>
        livre.leavesIn === null ? (
          <span className="muted" style={{ whiteSpace: "nowrap" }}>{t("livres.fimDaTemporada")}</span>
        ) : (
          <span className="num" style={{ color: livre.leavesIn <= 3 ? "var(--danger)" : "var(--text-muted)" }}>
            {t("livres.emRodadas", { rodadas: livre.leavesIn })}
          </span>
        ),
    },
    {
      chave: "pede",
      rotulo: t("livres.colunas.pede"),
      presenca: "destaque",
      largura: 96,
      direita: true,
      ordenar: (livre) => livre.asking ?? Number.MAX_SAFE_INTEGER,
      direcaoInicial: "asc",
      celula: (livre) => (livre.asking === null ? <span className="muted">{t("comum.vazio")}</span> : <Money value={livre.asking} />),
    },
  ];
}
